import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a Date to YYYY-MM-DD using local timezone (not UTC).
 * This avoids the common bug where toISOString() shifts the date
 * back by one day for users in UTC+ timezones.
 */
export function formatLocalDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Add minutes to a 24h "HH:MM" time string, wrapping within a day. */
export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':')
  const total = parseInt(h || '0', 10) * 60 + parseInt(m || '0', 10) + minutes
  const wrapped = ((total % 1440) + 1440) % 1440
  const hh = Math.floor(wrapped / 60)
  const mm = wrapped % 60
  return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`
}

/** Format a 24h "HH:MM" time as a 12h clock time, e.g. "8:30 PM". */
export function formatTime12h(time: string): string {
  const [h, m] = time.split(':')
  const hour = parseInt(h || '0', 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${m || '00'} ${ampm}`
}

/**
 * Format a start/end pair as a compact 12h range, e.g. "8:30–9:00 AM".
 * The AM/PM is shown once at the end when both times share it, and on
 * both ends when the range crosses noon or midnight.
 */
export function formatTimeRange(start: string, end: string): string {
  const startHour = parseInt(start.split(':')[0] || '0', 10)
  const endHour = parseInt(end.split(':')[0] || '0', 10)
  const sameMeridiem = startHour < 12 === endHour < 12
  const startLabel = sameMeridiem
    ? formatTime12h(start).replace(/ (AM|PM)$/, '')
    : formatTime12h(start)
  return `${startLabel}–${formatTime12h(end)}`
}

/**
 * Format a single bookable slot (a start time plus its duration) as a range,
 * e.g. "8:30–9:00 AM". Each slot label then shows exactly which half-hour
 * (or hour) it covers, removing the "does 8:30 mean until 9?" ambiguity.
 */
export function formatSlotRange(time: string, durationMin = 30): string {
  return formatTimeRange(time, addMinutesToTime(time, durationMin))
}

/**
 * Estimate the fees on a voluntary card donation so the guest can see, before
 * paying, roughly what the organisation will receive. The card fee uses Stripe's
 * standard UK rate (1.5% + 20p); the true figure comes back from Stripe after
 * payment, so these numbers are always labelled as estimates in the UI.
 */
export function estimateDonationBreakdown(
  amount: number,
  roomsiFeePercent: number
): { cardFee: number; roomsiFee: number; orgReceives: number } {
  const cardFee = Math.round((amount * 0.015 + 0.2) * 100) / 100
  const roomsiFee = Math.round(amount * (roomsiFeePercent / 100) * 100) / 100
  const orgReceives = Math.max(0, Math.round((amount - cardFee - roomsiFee) * 100) / 100)
  return { cardFee, roomsiFee, orgReceives }
}

/**
 * Group half-hour slot times into consecutive blocks, e.g.
 * ["14:00","14:30","15:00","18:00"] -> [["14:00","14:30","15:00"],["18:00"]].
 * Each block becomes one booking when a selection spans separate times.
 */
export function groupConsecutiveSlots(slots: string[]): string[][] {
  if (slots.length === 0) return []

  const sortedSlots = [...slots].sort()
  const blocks: string[][] = []
  let currentBlock: string[] = [sortedSlots[0]!]

  for (let i = 1; i < sortedSlots.length; i++) {
    const prevSlot = sortedSlots[i - 1]!
    const currentSlot = sortedSlots[i]!
    const [prevH = 0, prevM = 0] = prevSlot.split(':').map(Number)
    const [currH = 0, currM = 0] = currentSlot.split(':').map(Number)

    if (currH * 60 + currM - (prevH * 60 + prevM) === 30) {
      currentBlock.push(currentSlot)
    } else {
      blocks.push(currentBlock)
      currentBlock = [currentSlot]
    }
  }

  blocks.push(currentBlock)
  return blocks
}

/**
 * Convert a block of half-hour slots to its start/end times, e.g.
 * ["14:00","14:30","15:00"] -> { start: "14:00", end: "15:30" }.
 */
export function blockToTimeRange(block: string[]): { start: string; end: string } {
  const start = block[0] || '00:00'
  const lastSlot = block[block.length - 1] || start
  const [h = 0, m = 0] = lastSlot.split(':').map(Number)
  const endMinutes = h * 60 + m + 30
  const endH = Math.floor(endMinutes / 60)
  const endM = endMinutes % 60
  return { start, end: `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}` }
}
